import { ErrorLike, PropError } from "../../PropX";
import { ObjectWithValidationMessages } from "../../PropX";

type Result = { success: true; tokens: string[] } | { success: false; tokens: string[]; error: ErrorLike };

const splitRegex = /[\s,]+/;

/**
 * In order to produce same list of error messages as entering plain text into an input,
 * we create an input element and use its built-in validation. This is a bit hacky, but
 * it's the only way to access the browser's internal input validation logic.
 */
const createInput = (type: string = "text", pattern?: string) => {
  const input = document.createElement("input");
  input.type = type;

  if (pattern) {
    input.pattern = pattern;
  }

  return input;
};

/**
 * Splits tokens from a CSV string.
 */
export const splitCsvTokens = (csv: string, params: ObjectWithValidationMessages, pattern?: string, type?: string): Result => {
  let validate: (token: string) => ErrorLike | void;

  if (pattern || type) {
    const input = createInput(type, pattern);

    validate = (token: string) => {
      input.value = token;

      if (input.validity.valid) {
        return;
      }

      const message = PropError.getValidationMessageForInput(input, params);

      return { message };
    };
  } else {
    validate = () => undefined;
  }

  const tokens = csv
    .split(splitRegex)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);

  type IntermediateResult = {
    errors: string[];
    tokens: string[];
  };

  /**
   * Validate each token and add all bad tokens to a list so that we can display an error message.
   */
  const result = tokens.reduce(
    (acc, token) => {
      const maybeError = validate(token);

      if (maybeError) {
        const truncatedToken = token.length > 10 ? `${token.slice(0, 10)}...` : token;
        acc.errors.push(`"${truncatedToken}" is invalid (${maybeError.message})`);
      } else {
        acc.tokens.push(token);
      }

      return acc;
    },
    { errors: [], tokens: [] } as IntermediateResult
  );

  if (result.errors.length > 0) {
    const firstFive = result.errors.slice(0, 5).join(", ");
    const additionalErrors = result.errors.length > 5 ? `, and ${result.errors.length - 5} more.` : ".";
    const message = `${firstFive}${additionalErrors}`;

    return { success: false, error: { message }, tokens: result.tokens };
  }

  return { success: true, tokens: result.tokens };
};
